import React, {memo, ReactElement, useEffect, useMemo, useRef, useState} from 'react'
import styles from './UIGameChat.module.scss'
import feedStyles from './UIGameChatContainer/UIGameChatContainer.module.scss'
import {UIGameChatContainer} from './UIGameChatContainer/UIGameChatContainer'
import {UIGameFeedMessage} from '../UIGameFeedMessage/UIGameFeedMessage'
import {GameValidator} from '@colonist/game-mechanic-validators'
import {GameChatMessageData, NestedReadonly, GameChatType} from '@colonist/game-states'
import {createFeedMessageDataForGameChatData} from '../UIGameFeedMessage/FeedMessageDataChats'
import {UIGameChatDebugMessage} from './UIGameChatDebugMessage'
import {t} from '@colonist/ui'
// eslint-disable-next-line no-restricted-imports
import {Sound} from '../../../../../../code/client/shared/scripts/Sound'
// eslint-disable-next-line no-restricted-imports
import {ClientUserState} from '../../../../../../code/client/shared/scripts/ClientUserState'
import {UIGameFeedVirtualScrollContainer} from '../UIGameFeedVirtualScrollContainer/UIGameFeedVirtualScrollContainer'
import clsx from 'clsx'
import IconSend from '@static/images/icon_send.svg'
import IconDownArrowBlack from '@static/images/icon_arrow_down_black.svg'
import {useGameStore} from '../../../store/GameStoreContext'
import {useHasOnlyOneHumanPlayer} from '../../../store/selectors/useHasOnlyOneHumanPlayer'
import {usePlayerValidators} from '../../../store/selectors/usePlayerValidators'
import {useIsReplay} from '../../../store/selectors/useIsReplay'

const UIGameChatFeedMessage = memo(function UIGameChatFeedMessage({
    gameValidator,
    gameChatData,
}: {
    gameValidator: GameValidator
    gameChatData: NestedReadonly<GameChatMessageData>
}) {
    const feedMessageData = createFeedMessageDataForGameChatData(gameValidator, gameChatData)

    return (
        <UIGameFeedMessage
            message={feedMessageData}
        />
    )
})

const FIRST_MESSAGE_LOCAL_KEY = 'local0'
const monitoringChatData: GameChatMessageData = {
    type: GameChatType.SystemMessage,
    message: `Chat is being monitored. Please be respectful.`,
}

// TODO: @Dang remove this when SocketWebActionController fully migrated to React.
let globalAddSpectatorMessage: undefined | ((data: GameChatMessageData) => void)
export function addGlobalSpectatorMessage(data: GameChatMessageData) {
    if(globalAddSpectatorMessage) globalAddSpectatorMessage(data)
}

export type UIGameChatBackgroundColor = 'blue' | 'beige'

export function UIGameChat({
    isChatVisible,
    onSendChat,
    isDefaultMinimized = false,
    isMinimizeable,
    gameValidator,
    backgroundColor,
}: {
    isChatVisible: boolean
    onSendChat: (message: string) => void
    isDefaultMinimized?: boolean | undefined
    isMinimizeable: boolean
    gameValidator: GameValidator
    backgroundColor: UIGameChatBackgroundColor
}) {
    const gameChatState = useGameStore((state) => state.gameState.gameChatState)
    const {getPlayerValidator} = usePlayerValidators()

    const isPrivateGame = useGameStore((state) => state.gameSettings.isPrivateGame)
    const isMyColorSpectator = useGameStore((state) => state.isMyColorSpectator)

    const [isMinimized, setIsMinimized] = useState(isDefaultMinimized)
    const [wasChatMonitoringWarningShown, setWasChatMonitoringWarningShown] = useState(false)

    const inputRef = useRef<HTMLInputElement>(null)
    const [localMessages, setLocalMessages] = useState<{[key: string]: ReactElement[]}>({})
    const mutedPlayers = useGameStore((state) => state.gameMute.mutedPlayers)
    const hasOnlyOneHumanPlayer = useHasOnlyOneHumanPlayer()
    const inCohortToSeeChatIsBeingMonitoredMessage = ClientUserState.abTests.CHAT_TOXICITY_SHOW_MONITORED_WARNING == 'SHOW_CHAT_IS_MONITORED_WARNING'
    const isReplay = useIsReplay()

    globalAddSpectatorMessage = (data: GameChatMessageData) => {
        const messageElement = <UIGameChatFeedMessage
            gameChatData={data}
            gameValidator={gameValidator}
        />
        sendLocalMessage(messageElement)
    }

    const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        const message = inputRef.current?.value.trim() ?? ''
        if(message === '') return

        if(inputRef.current) {
            inputRef.current.value = ''
        }

        if(message === '/debug') sendLocalMessage(<UIGameChatDebugMessage />)
        else onSendChat(message)
    }

    const sendLocalMessage = (message: ReactElement) => {
        const key = Object.keys(gameChatState).at(-1) ?? FIRST_MESSAGE_LOCAL_KEY
        const localMessagesAtKey = localMessages[key] ?? []
        const messageWithKey = <div key={`${key}-${localMessagesAtKey.length}`}>
            {message}
        </div>

        setLocalMessages({
            ...localMessages,
            [key]: [...localMessagesAtKey, messageWithKey],
        })
    }

    useEffect(() => {
        if(isMyColorSpectator) return
        if(isMinimized) return
        if(isPrivateGame) return
        if(wasChatMonitoringWarningShown) return
        if(hasOnlyOneHumanPlayer === true) return
        if(!inCohortToSeeChatIsBeingMonitoredMessage) return

        setWasChatMonitoringWarningShown(true)

        sendLocalMessage(<UIGameChatFeedMessage
            gameChatData={monitoringChatData}
            gameValidator={gameValidator}
        />)
    }, [isMinimized, wasChatMonitoringWarningShown])

    const messages = useMemo(() => {
        const msgs: ReactElement[] = []
        if(localMessages[FIRST_MESSAGE_LOCAL_KEY] !== undefined) msgs.push(...localMessages[FIRST_MESSAGE_LOCAL_KEY])
        for(const key in gameChatState) {
            const gameChatData = gameChatState[key].text
            if(gameChatData.type === GameChatType.Message && gameChatData.from != null) {
                const username = getPlayerValidator(gameChatData.from)?.username
                if(username != null && mutedPlayers.includes(username)) continue
            }

            msgs.push(<UIGameChatFeedMessage
                gameChatData={gameChatData}
                gameValidator={gameValidator}
                key={key}
            />)

            const localMessagesAtKey = localMessages[key]
            if(localMessagesAtKey !== undefined) msgs.push(...localMessagesAtKey)
        }
        return msgs
    }, [gameChatState, localMessages, mutedPlayers])

    const numberOfMessages = Object.keys(messages).length
    const prevMessagesCountRef = React.useRef(numberOfMessages)
    const hasNewMessages = numberOfMessages > prevMessagesCountRef.current

    useEffect(() => {
        if(hasNewMessages) {
            Sound.playSound(Sound.SoundType.MessageNotification)
        }
        prevMessagesCountRef.current = numberOfMessages
    }, [hasNewMessages, numberOfMessages, mutedPlayers])

    const [numberOfReadMessages, setNumberOfReadMessages] = useState(numberOfMessages)

    const numberOfUnreadMessages = useMemo(() => numberOfMessages - numberOfReadMessages, [numberOfReadMessages, numberOfMessages])

    useEffect(() => {
        if(isMinimized) return

        setNumberOfReadMessages(numberOfMessages)
    }, [isMinimized, numberOfMessages])

    const showMinimizedNotification = numberOfUnreadMessages > 0

    const isAfterLiveGame = useGameStore((state) => state.gameEnd.isAfterLiveGame)
    const isChatInputDisabled = isReplay && !isAfterLiveGame

    if(!isChatVisible) return null

    return (
        <UIGameChatContainer
            isMinimized={isMinimized && isMinimizeable}
            minimizedTitle={t('strings:imgAlts.Chat')}
            setIsMinimized={setIsMinimized}
            showMinimizedNotification={showMinimizedNotification}
            backgroundColor={backgroundColor}>
            <div className={styles.chatContainer}>
                <UIGameFeedVirtualScrollContainer autoScrollToBottom>
                    {messages}
                </UIGameFeedVirtualScrollContainer>
                <div className={styles.chatBottomContainer}>
                    <form className={styles.formChat} onSubmit={handleSubmit}>
                        <input
                            className={clsx(styles.inputText, {
                                [styles.blue]: backgroundColor === 'blue',
                            })}
                            data-testid='game-chat-input'
                            maxLength={200}
                            placeholder={t('strings:homePage.header.sendMessage')}
                            ref={inputRef}
                            disabled={isChatInputDisabled}
                            type="text" />
                        <button
                            className={clsx(styles.buttonSubmit, {
                                [styles.blue]: backgroundColor === 'blue',
                                [styles.beige]: backgroundColor === 'beige',
                            })}
                            disabled={isChatInputDisabled}
                            data-testid='game-chat-submit'>
                            <img src={IconSend} />
                        </button>

                    </form>
                    {isMinimizeable && <div
                        className={feedStyles.chatMinimizeButton}
                        onClick={() => setIsMinimized(true)}>
                        <img src={IconDownArrowBlack} />
                    </div>}
                </div>
            </div>
        </UIGameChatContainer>
    )
}
